import React, { useState, useEffect, useContext } from "react";
import { Form, Button, Row, Col, Table, Card } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import axios from "../../../../config/axios";
import { Context } from "../../../../context/Context";
import { simpleAlert } from "../../../alerts/Alerts";

export const ProductEditForm = ({ orderId }) => {
  const [auth] = useContext(Context);
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    vendor: "",
    product_type: "",
    status: "",
  });

  const [inventories, setInventories] = useState([]);
  const [prices, setPrices] = useState([]);
  const [isTitleEditable, setIsTitleEditable] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const { data } = await axios.get(`/product/detail/user/${auth.id}`, {
          params: { product_id: orderId },
        });

        const prod = data.rows?.[0];

        if (prod && prod.variants?.length > 0) {
          setProduct(prod);

          setFormData({
            title: prod.title || "",
            description: prod.description || "",
            vendor: prod.vendor || "",
            product_type: prod.product_type || "",
            status: prod.status || "",
          });

          setInventories(
            prod.variants.map((v) => ({
              id: v.id,
              sku: v.sku || "",
              variant_1: v.variant_1 || "",
              variant_2: v.variant_2 || "",
              option_1: v.option_1 || "",
              option_2: v.option_2 || "",
              inventory_quantity: v.inventory_quantity || 0,
              weight: v.weight || 0,
              weight_unit: v.weight_unit || "g",
            }))
          );

          const firstVariant = prod.variants[0];
          setPrices(
            firstVariant.prices?.map((price) => ({
              id: price.id,
              price_list_id: price.price_list_id,
              price_list_name: price.price_list?.name,
              price: price.price || 0,
              compare_at_price: price.compare_at_price || 0,
              currency: price.currency || "COP",
            })) || []
          );
        }
      } catch (error) {
        console.error("Error al obtener el producto:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [auth.id, orderId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInventoryChange = (index, field, value) => {
    setInventories((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, [field]: field === "inventory_quantity" || field === "weight" ? parseFloat(value) : value }
          : item
      )
    );
  };

  const handlePriceChange = (index, field, value) => {
    setPrices((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      product: {
        id: product.id,
        title: formData.title,
        description: formData.description,
        vendor: formData.vendor,
        product_type: formData.product_type,
        status: formData.status,
      },
      variants: inventories.map((inv) => ({
        id: inv.id,
        sku: inv.sku,
        variant_1: inv.variant_1,
        variant_2: inv.variant_2,
        option_1: inv.option_1,
        option_2: inv.option_2,
        inventory_quantity: parseInt(inv.inventory_quantity),
        weight: parseFloat(inv.weight),
        weight_unit: inv.weight_unit,
      })),
      prices: prices.map((p) => ({
        id: p.id,
        price_list_id: p.price_list_id,
        price: parseFloat(p.price),
        compare_at_price: parseFloat(p.compare_at_price),
      })),
    };

    try {
      await axios.put(`/product/update/user/${auth.id}`, payload);
      simpleAlert('Actualizado', 'success', 'Producto actualizado correctamente');
    } catch (error) {
      console.error("Error al actualizar el producto:", error);
      simpleAlert('Error', 'error', 'Error al actualizar el producto');
    }
  };

  if (loading) return <p>Cargando producto...</p>;
  if (!product) return <p>Producto no encontrado.</p>;

  return (
    <Form onSubmit={handleSubmit}>
      <h5 className="mb-3">Información general</h5>

      <Form.Group className="mb-3">
        <Form.Label className="d-flex justify-content-between align-items-center">
          Nombre del producto
          <div className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
            <Form.Check
              type="checkbox"
              id="editTitle"
              checked={isTitleEditable}
              onChange={() => setIsTitleEditable(!isTitleEditable)}
              className="me-2"
            />
            <FontAwesomeIcon icon={faPen} title="Permitir edición" />
          </div>
        </Form.Label>
        <Form.Control
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          disabled={!isTitleEditable}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Descripción</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          name="description"
          value={formData.description}
          onChange={handleChange}
        />
      </Form.Group>

      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Proveedor</Form.Label>
            <Form.Control
              type="text"
              name="vendor"
              value={formData.vendor}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Tipo de producto</Form.Label>
            <Form.Control
              type="text"
              name="product_type"
              value={formData.product_type}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-4">
        <Form.Label>Estado</Form.Label>
        <Form.Select name="status" value={formData.status} onChange={handleChange}>
          <option value="active">Activo</option>
          <option value="draft">Borrador</option>
          <option value="archived">Archivado</option>
        </Form.Select>
      </Form.Group>

      <h5 className="mt-4 mb-3">Inventario por variante</h5>
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Table bordered hover>
          <thead>
            <tr>
              <th>SKU</th>
              <th>Variante 1 / Opción 1</th>
              <th>Variante 2 / Opción 2</th>
              <th>Cantidad</th>
            </tr>
          </thead>
          <tbody>
            {inventories.map((item, index) => (
              <tr key={item.id}>
                <td>
                  <Form.Control
                    type="text"
                    value={item.sku}
                    onChange={(e) =>
                      handleInventoryChange(index, "sku", e.target.value)
                    }
                  />
                </td>
                <td>
                  <div style={{ fontWeight: 'bold' }}>{item.variant_1}</div>
                  <div style={{ fontSize: '0.85em', color: '#666' }}>{item.option_1}</div>
                </td>
                <td>
                  <div style={{ fontWeight: 'bold' }}>{item.variant_2}</div>
                  <div style={{ fontSize: '0.85em', color: '#666' }}>{item.option_2}</div>
                </td>
                <td>
                  <Form.Control
                    type="number"
                    value={item.inventory_quantity}
                    style={{
                      color: item.inventory_quantity === 0 ? 'red' : 'inherit',
                      borderColor: item.inventory_quantity === 0 ? 'red' : undefined,
                    }}
                    onChange={(e) =>
                      handleInventoryChange(index, "inventory_quantity", e.target.value)
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <h5 className="mt-4 mb-3">Precios por lista</h5>
      <Card border="light" className="table-wrapper table-responsive shadow-sm">
        <Table bordered hover>
          <thead>
            <tr>
              <th>Lista de precios</th>
              <th>Precio</th>
              <th>Precio comparativo</th>
              <th>Moneda</th>
            </tr>
          </thead>
          <tbody>
            {prices.map((price, index) => (
              <tr key={price.id}>
                <td>{price.price_list_name}</td>
                <td>
                  <Form.Control
                    type="number"
                    value={price.price}
                    onChange={(e) =>
                      handlePriceChange(index, "price", e.target.value)
                    }
                  />
                </td>
                <td>
                  <Form.Control
                    type="number"
                    value={price.compare_at_price}
                    onChange={(e) =>
                      handlePriceChange(index, "compare_at_price", e.target.value)
                    }
                  />
                </td>
                <td>{price.currency}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Button variant="primary" type="submit" className="w-100 mt-3">
        Guardar cambios
      </Button>
    </Form>
  );
};

export default ProductEditForm;
